import { TARGET_DATA_TYPES, TARGET_STATUSES } from 'src/constants/targets';

/**
 * @typedef {Object} SbtEoTarget
 * @property {string} id
 * @property {string} [label]
 * @property {string} [dataType]
 * @property {string|null} [parentTarget]
 * @property {string} [status]
 * @property {SbtEoTarget[]} [subTargets]
 */

/**
 * A randomRotation with no displayable sub-targets has nothing to rotate
 * to, so it renders an empty, unclickable card. Treat it as not score able.
 *
 * @param {SbtEoTarget} target
 * @returns {boolean}
 */
export function hasScoreableContent(target) {
  if (target?.dataType === TARGET_DATA_TYPES.RANDOM_ROTATION) {
    return (target.subTargets || []).length > 0;
  }
  return true;
} // hasScoreableContent()

/**
 * Builds the ordered EO candidate list (top-level steps + non-mastered branch sub-targets).
 *
 * @param {Object} params
 * @param {SbtEoTarget[]} params.sortedTargets
 * @param {string|null} params.srTargetId
 * @param {string|null} params.cabBranchId
 * @returns {SbtEoTarget[]}
 */
export function buildEoCandidates({ sortedTargets, srTargetId, cabBranchId }) {
  const targets = sortedTargets || [];

  const topLevelSteps = targets.filter(
    (target) => target.dataType !== TARGET_DATA_TYPES.CAB_BRANCH && target.id !== srTargetId,
  );

  const branch = targets.find((target) => target.id === cabBranchId);
  const branchSubTargets = (branch?.subTargets || []).filter(
    (target) => target.status !== TARGET_STATUSES.MASTERED,
  );

  return [...topLevelSteps, ...branchSubTargets].filter(hasScoreableContent);
} // buildEoCandidates()

/**
 * @param {SbtEoTarget} target
 * @returns {SbtEoTarget|null}
 */
function lastSubTarget(target) {
  const subTargets = target?.subTargets || [];
  return subTargets[subTargets.length - 1] ?? null;
} // lastSubTarget()

/**
 * @param {SbtEoTarget} target
 * @returns {string|null}
 */
function lastSubTargetId(target) {
  return lastSubTarget(target)?.id ?? null;
}

/**
 * Resolves the actual target ID to be recorded in the data row for a candidate step
 * (resolves sub-target IDs for shaping and randomRotation).
 *
 * @param {SbtEoTarget|null} picked
 * @param {Record<string, string|null>} rrTargetUpdates
 * @returns {string|null}
 */
function resolveScoredTargetId(picked, rrTargetUpdates) {
  if (!picked) {
    return null;
  }
  if (picked.dataType === TARGET_DATA_TYPES.SHAPING) {
    return lastSubTargetId(picked);
  }
  if (picked.dataType === TARGET_DATA_TYPES.RANDOM_ROTATION) {
    return rrTargetUpdates[`rrTarget_${picked.id}`] ?? null;
  }
  return picked.id ?? null;
} // resolveScoredTargetId()

/**
 * Rolls a random sub-target for each randomRotation candidate in the list.
 *
 * @param {SbtEoTarget[]} candidates
 * @param {() => number} random
 * @returns {Record<string, string|null>}
 */
function buildRrTargetUpdates(candidates, random) {
  return candidates
    .filter((target) => target.dataType === TARGET_DATA_TYPES.RANDOM_ROTATION && target.id)
    .reduce((updates, target) => {
      const subTargets = target.subTargets || [];
      const pickedSubTarget = subTargets.length > 0
        ? subTargets[Math.floor(random() * subTargets.length)]
        : null;
      return { ...updates, [`rrTarget_${target.id}`]: pickedSubTarget?.id ?? null };
    }, {});
} // buildRrTargetUpdates()

/**
 * Resolves a candidate index into session program data and target response for mastery scoring.
 *
 * @param {Object} params
 * @param {SbtEoTarget[]} params.candidates
 * @param {number} params.index
 * @param {string|null} params.cabBranchId
 * @param {SbtEoTarget[]} [params.programTargets] flat `sessionProgram.program.targets`, used to
 *   check whether a rotated RR target response is mastered/closed
 * @param {boolean} [params.rotate] false mirrors `rotateTarget`'s `disable` guard: no RR keys at all
 * @param {() => number} [params.random]
 * @param {string|null} [params.forcedSubTargetId] a manually picked randomRotation child, which
 *   overrides that target's roll. Ignored for any other candidate type.
 * @returns {{ programData: { currentEOTarget: string|null, currentCABBranch: string|null,
 *   [rrKey: string]: string|null }, targetResponse: string|null, scoredTargetId: string|null }}
 *   `scoredTargetId` is the leaf id the recorded row will carry — see `resolveScoredTargetId`.
 */
export function resolveEoTask({
  candidates, index, cabBranchId, programTargets = [], rotate = true, random = Math.random,
  forcedSubTargetId = null,
}) {
  const picked = candidates[index] ?? null;
  const currentEOTarget = picked?.id ?? null;

  let targetResponse = null;
  if (picked && index === candidates.length - 1) {
    targetResponse = picked.dataType === TARGET_DATA_TYPES.SHAPING
      ? lastSubTargetId(picked)
      : currentEOTarget;
  }

  // Not in a CAB when the picked candidate is a top-level step.
  const currentCABBranch = picked?.parentTarget == null ? null : cabBranchId;

  const rrTargetUpdates = rotate ? buildRrTargetUpdates(candidates, random) : {};

  // Override roll with manually picked rotation child if provided
  if (rotate && forcedSubTargetId && picked?.id
    && (picked.dataType === TARGET_DATA_TYPES.RANDOM_ROTATION)) {
    rrTargetUpdates[`rrTarget_${picked.id}`] = forcedSubTargetId;
  }

  // Handle RR target response status check
  const rrTargetResponse = rrTargetUpdates[`rrTarget_${targetResponse}`] ?? null;
  if (rrTargetResponse) {
    const rrTargetStatus = programTargets.find((target) => target.id === rrTargetResponse)?.status;
    targetResponse = [TARGET_STATUSES.MASTERED, TARGET_STATUSES.CLOSED].includes(rrTargetStatus)
      ? null
      : rrTargetResponse;
  }

  return {
    programData: { currentEOTarget, currentCABBranch, ...rrTargetUpdates },
    targetResponse,
    scoredTargetId: resolveScoredTargetId(picked, rrTargetUpdates),
  };
} // resolveEoTask()

/**
 * Maps a single candidate target into option rows for the "Select step" menu.
 *
 * @param {SbtEoTarget} candidate
 * @param {string|null} branchId
 * @param {(target: SbtEoTarget) => string|null} getLabel
 * @returns {Array<{ label: string|null, caption: string|null, targetId: string,
 *   branchId: string|null, subTargetId: string|null }>}
 */
function buildCandidateOptions(candidate, branchId, getLabel) {
  if (candidate.dataType === TARGET_DATA_TYPES.RANDOM_ROTATION) {
    return (candidate.subTargets || [])
      .filter((subTarget) => subTarget.status !== TARGET_STATUSES.MASTERED)
      .map((subTarget) => ({
        label: getLabel(subTarget),
        caption: getLabel(candidate),
        targetId: candidate.id,
        branchId,
        subTargetId: subTarget.id,
      }));
  }

  if (candidate.dataType === TARGET_DATA_TYPES.SHAPING) {
    const activeSubTarget = lastSubTarget(candidate);
    if (activeSubTarget) {
      return [{
        label: getLabel(activeSubTarget),
        caption: getLabel(candidate),
        targetId: candidate.id,
        branchId,
        subTargetId: null,
      }];
    }
  }

  return [{
    label: getLabel(candidate),
    caption: null,
    targetId: candidate.id,
    branchId,
    subTargetId: null,
  }];
} // buildCandidateOptions()

/**
 * Builds grouped step options for the "Select step" menu (top-level steps and active CAB branches).
 *
 * @param {Object} params
 * @param {SbtEoTarget[]} params.sortedTargets
 * @param {string|null} params.srTargetId
 * @param {SbtEoTarget[]} params.cabBranches
 * @param {(target: SbtEoTarget) => string|null} params.getLabel
 * @returns {Array<{ subHeader: string|null,
 *   options: Array<{ label: string|null, caption: string|null, targetId: string,
 *     branchId: string|null, subTargetId: string|null }> }>}
 */
export function buildEoStepGroups({
  sortedTargets, srTargetId, cabBranches, getLabel,
}) {
  const groups = [];

  const topLevelOptions = buildEoCandidates({ sortedTargets, srTargetId, cabBranchId: null })
    .flatMap((candidate) => buildCandidateOptions(candidate, null, getLabel));
  if (topLevelOptions.length > 0) {
    groups.push({ subHeader: null, options: topLevelOptions });
  }

  (cabBranches || []).forEach((branch) => {
    const branchOptions = buildEoCandidates({ sortedTargets, srTargetId, cabBranchId: branch.id })
      .filter((target) => target.parentTarget === branch.id)
      .flatMap((candidate) => buildCandidateOptions(candidate, branch.id, getLabel));
    if (branchOptions.length === 0) {
      return;
    }
    groups.push({ subHeader: getLabel(branch), options: branchOptions });
  });

  return groups;
} // buildEoStepGroups()

/**
 * Whether a menu option stands for the EO task currently queued up, so the menu can mark it
 * active.
 *
 * @param {Object} params
 * @param {{ targetId: string, subTargetId: string|null }|null} params.option
 * @param {string|null} params.currentEOTarget
 * @param {string|null} params.rotatedSubTargetId value of `rrTarget_<option.targetId>` on the
 *   session doc; null for a candidate that does not rotate
 * @returns {boolean}
 */
export function isEoStepSelected({ option, currentEOTarget, rotatedSubTargetId }) {
  if (!option || !currentEOTarget || option.targetId !== currentEOTarget) {
    return false;
  }
  // A randomRotation option names its parent as targetId, so matching that alone would light up
  // every one of its children — it also has to be the child the parent is rotated to.
  if (option.subTargetId == null) {
    return true;
  }
  return option.subTargetId === rotatedSubTargetId;
}

/**
 * Splits the two EO labels into the branch line and the step name the card renders separately.
 *
 * Only a CAB branch carries a sub-task, and in that case the task label names the branch while the
 * sub-task label names the step. Every other EO task has no branch and its task label *is* the step.
 *
 * @param {Object} params
 * @param {string|null} params.taskLabel label of the queued EO task
 * @param {string|null} params.subTaskLabel label of its active sub-target, null when it has none
 * @returns {{ branchLabel: string|null, stepLabel: string|null }}
 */
export function splitEoLabels({ taskLabel, subTaskLabel }) {
  if (subTaskLabel) {
    return { branchLabel: taskLabel ?? null, stepLabel: subTaskLabel };
  }
  return { branchLabel: null, stepLabel: taskLabel ?? null };
}
